import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { ventasAPI, lotesAPI, authAPI, reportsAPI } from '../lib/api';
import { formatNumber, formatCurrency } from '../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { toast } from 'sonner';
import {
  Plus,
  Receipt,
  MagnifyingGlass,
  CheckCircle,
  Clock,
  Warning,
  CurrencyDollar,
  Scales,
  Bird,
  Trash,
  Eye,
  Lightning,
  Money,
  CaretDown,
  ListBullets,
  PencilSimple,
  ShieldCheck,
  FilePdf,
  Printer,
} from '@phosphor-icons/react';

export default function Ventas() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const loteIdFilter = searchParams.get('lote_id');
  
  const [ventas, setVentas] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroEstado, setFiltroEstado] = useState('todos');
  
  // Modal de pago
  const [showPagoModal, setShowPagoModal] = useState(false);
  const [ventaSeleccionada, setVentaSeleccionada] = useState(null);
  const [montoPago, setMontoPago] = useState('');
  const [notaPago, setNotaPago] = useState('');
  const [loadingPago, setLoadingPago] = useState(false);
  const [filtroLote, setFiltroLote] = useState(loteIdFilter || 'todos');
  const [busqueda, setBusqueda] = useState('');
  const [resumen, setResumen] = useState(null);
  
  // Modal de edición/eliminación de pago
  const [showEditPagoModal, setShowEditPagoModal] = useState(false);
  const [pagoAEditar, setPagoAEditar] = useState(null);
  const [ventaDelPago, setVentaDelPago] = useState(null);
  const [editPagoCode, setEditPagoCode] = useState('');
  const [editPagoMonto, setEditPagoMonto] = useState('');
  const [editPagoNota, setEditPagoNota] = useState('');
  const [savingPagoEdit, setSavingPagoEdit] = useState(false);
  
  // Modal de factura
  const [showFacturaModal, setShowFacturaModal] = useState(false);
  const [ventaFactura, setVentaFactura] = useState(null);
  
  // Fila expandida para ver detalles
  const [expandedRowId, setExpandedRowId] = useState(null);
  
  // Selección múltiple de facturas
  const [facturasSeleccionadas, setFacturasSeleccionadas] = useState([]);
  const [showResumenSeleccion, setShowResumenSeleccion] = useState(false);
  
  // Modal de edición - Super Usuario
  const [showEditModal, setShowEditModal] = useState(false);
  const [ventaAEditar, setVentaAEditar] = useState(null);
  const [superuserCode, setSuperuserCode] = useState('');
  const [validatingSuperuser, setValidatingSuperuser] = useState(false);
  const [superuserValidated, setSuperuserValidated] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [ventasRes, lotesRes] = await Promise.all([
        ventasAPI.getAll({ lote_id: loteIdFilter || undefined }),
        lotesAPI.getAll()
      ]);
      setVentas(ventasRes.data);
      setLotes(lotesRes.data);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [loteIdFilter]);

  const loadResumen = useCallback(async (loteId) => {
    try {
      const res = await ventasAPI.getResumenLote(loteId);
      setResumen(res.data);
    } catch (error) {
      // Error silenciado en producción
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (filtroLote && filtroLote !== 'todos') {
      loadResumen(filtroLote);
    } else {
      setResumen(null);
    }
  }, [filtroLote, loadResumen]);

  const abrirModalPago = (venta) => {
    setVentaSeleccionada(venta);
    setMontoPago(venta.saldo_pendiente?.toString() || venta.total_factura.toString());
    setNotaPago('');
    setShowPagoModal(true);
  };

  const cerrarModalPago = () => {
    setShowPagoModal(false);
    setVentaSeleccionada(null);
    setMontoPago('');
    setNotaPago('');
  };

  const handlePagar = async () => {
    if (!ventaSeleccionada) return;
    
    const monto = parseFloat(montoPago);
    if (isNaN(monto) || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    const saldo = ventaSeleccionada.saldo_pendiente || ventaSeleccionada.total_factura;
    if (monto > saldo) {
      toast.error(`El monto no puede exceder el saldo pendiente de ${formatCurrency(saldo)}`);
      return;
    }

    setLoadingPago(true);
    try {
      await ventasAPI.registrarPago(ventaSeleccionada.id, { monto, nota: notaPago });
      toast.success(monto >= saldo ? 'Pago completo registrado' : 'Abono registrado correctamente');
      cerrarModalPago();
      loadData();
      if (filtroLote !== 'todos') {
        loadResumen(filtroLote);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al registrar pago';
      toast.error(message);
    } finally {
      setLoadingPago(false);
    }
  };

  // Funciones para editar/eliminar pagos
  const abrirEditarPago = (venta, pago) => {
    setVentaDelPago(venta);
    setPagoAEditar(pago);
    setEditPagoMonto(pago.monto.toString());
    setEditPagoNota(pago.nota || '');
    setEditPagoCode('');
    setShowEditPagoModal(true);
  };

  const cerrarEditarPago = () => {
    setShowEditPagoModal(false);
    setPagoAEditar(null);
    setVentaDelPago(null);
    setEditPagoCode('');
    setEditPagoMonto('');
    setEditPagoNota('');
  };

  const handleEditarPago = async () => {
    if (!pagoAEditar || !ventaDelPago || !editPagoCode) {
      toast.error('Complete todos los campos');
      return;
    }

    const monto = parseFloat(editPagoMonto);
    if (isNaN(monto) || monto <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    setSavingPagoEdit(true);
    try {
      await ventasAPI.editarPago(ventaDelPago.id, pagoAEditar.id, { monto, nota: editPagoNota }, editPagoCode);
      toast.success('Pago actualizado correctamente');
      cerrarEditarPago();
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al editar pago';
      toast.error(message);
    } finally {
      setSavingPagoEdit(false);
    }
  };

  const handleEliminarPago = async () => {
    if (!pagoAEditar || !ventaDelPago || !editPagoCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }

    if (!window.confirm('¿Está seguro de eliminar este pago?')) return;

    setSavingPagoEdit(true);
    try {
      await ventasAPI.eliminarPago(ventaDelPago.id, pagoAEditar.id, editPagoCode);
      toast.success('Pago eliminado correctamente');
      cerrarEditarPago();
      loadData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al eliminar pago';
      toast.error(message);
    } finally {
      setSavingPagoEdit(false);
    }
  };

  const handleDelete = async (ventaId) => {
    if (!window.confirm('¿Está seguro de eliminar esta venta?')) return;
    try {
      await ventasAPI.delete(ventaId);
      toast.success('Venta eliminada');
      loadData();
    } catch (error) {
      toast.error('Error al eliminar venta');
    }
  };

  // Funciones para editar ventas con validación de super usuario
  const abrirModalEdicion = (venta) => {
    setVentaAEditar(venta);
    setSuperuserCode('');
    setSuperuserValidated(false);
    setEditFormData({
      cliente: venta.cliente,
      fecha: venta.fecha.split('T')[0],
      total_jaulas: venta.total_jaulas,
      cantidad_pollos: venta.cantidad_pollos,
      peso_tara: venta.peso_tara,
      peso_bruto: venta.peso_bruto,
      precio_libra: venta.precio_libra,
      dias_credito: venta.dias_credito,
    });
    setShowEditModal(true);
  };

  const cerrarModalEdicion = () => {
    setShowEditModal(false);
    setVentaAEditar(null);
    setSuperuserCode('');
    setSuperuserValidated(false);
    setEditFormData({});
  };

  // Función para imprimir/descargar factura como PDF
  const handlePrintFactura = async (venta) => {
    try {
      toast.info('Generando factura PDF...');
      await reportsAPI.downloadFactura(venta.id, venta.no_conduce);
      toast.success('Factura descargada correctamente');
    } catch (error) {
      toast.error('Error al generar la factura');
    }
  };

  // Funciones para selección múltiple de facturas
  const toggleFacturaSeleccion = (venta) => {
    setFacturasSeleccionadas(prev => {
      const existe = prev.find(f => f.id === venta.id);
      if (existe) {
        return prev.filter(f => f.id !== venta.id);
      } else {
        return [...prev, venta];
      }
    });
  };

  const seleccionarTodas = () => {
    if (facturasSeleccionadas.length === ventasFiltradas.length) {
      setFacturasSeleccionadas([]);
    } else {
      setFacturasSeleccionadas([...ventasFiltradas]);
    }
  };

  const limpiarSeleccion = () => {
    setFacturasSeleccionadas([]);
  };

  // Calcular totales de facturas seleccionadas
  const resumenSeleccion = {
    cantidad: facturasSeleccionadas.length,
    totalFacturado: facturasSeleccionadas.reduce((sum, f) => sum + (f.total_factura || 0), 0),
    totalPagado: facturasSeleccionadas.reduce((sum, f) => sum + (f.monto_pagado || 0), 0),
    totalPendiente: facturasSeleccionadas.reduce((sum, f) => sum + (f.saldo_pendiente || 0), 0),
    pollosTotal: facturasSeleccionadas.reduce((sum, f) => sum + (f.cantidad_pollos || 0), 0),
    pesoNetoTotal: facturasSeleccionadas.reduce((sum, f) => sum + (f.peso_neto || 0), 0),
  };

  // Generar PDF de estado de cuenta
  const handleGenerarEstadoCuenta = async () => {
    if (facturasSeleccionadas.length === 0) {
      toast.error('Seleccione al menos una factura');
      return;
    }
    try {
      toast.info('Generando estado de cuenta...');
      const ids = facturasSeleccionadas.map(f => f.id);
      await reportsAPI.downloadEstadoCuenta(ids);
      toast.success('Estado de cuenta descargado');
    } catch (error) {
      toast.error('Error al generar estado de cuenta');
    }
  };

  const handleValidateSuperuser = async () => {
    if (!superuserCode) {
      toast.error('Ingrese el código de super usuario');
      return;
    }
    setValidatingSuperuser(true);
    try {
      await authAPI.validateSuperuser(superuserCode);
      setSuperuserValidated(true);
      toast.success('Código validado correctamente');
    } catch (error) {
      const message = error.response?.data?.detail || 'Código inválido';
      toast.error(message);
    } finally {
      setValidatingSuperuser(false);
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditFormData(prev => ({ ...prev, [field]: value }));
  };

  // Calcular valores derivados para el formulario de edición
  const editPesoNeto = (parseFloat(editFormData.peso_bruto) || 0) - (parseFloat(editFormData.peso_tara) || 0);
  const editPesoPromedio = editFormData.cantidad_pollos ? editPesoNeto / parseFloat(editFormData.cantidad_pollos) : 0;
  const editTotalFactura = editPesoNeto * (parseFloat(editFormData.precio_libra) || 0);

  const handleSaveEdit = async () => {
    if (!ventaAEditar || !superuserValidated) return;
    
    setSavingEdit(true);
    try {
      const updateData = {
        cliente: editFormData.cliente,
        fecha: editFormData.fecha,
        total_jaulas: parseInt(editFormData.total_jaulas) || 0,
        cantidad_pollos: parseInt(editFormData.cantidad_pollos),
        peso_tara: parseFloat(editFormData.peso_tara),
        peso_bruto: parseFloat(editFormData.peso_bruto),
        precio_libra: parseFloat(editFormData.precio_libra),
        dias_credito: parseInt(editFormData.dias_credito) || 0,
      };
      
      await ventasAPI.update(ventaAEditar.id, updateData, superuserCode);
      toast.success('Venta actualizada correctamente');
      cerrarModalEdicion();
      loadData();
      if (filtroLote !== 'todos') {
        loadResumen(filtroLote);
      }
    } catch (error) {
      const message = error.response?.data?.detail || 'Error al actualizar la venta';
      toast.error(message);
    } finally {
      setSavingEdit(false);
    }
  };

  // Filter ventas
  const ventasFiltradas = ventas.filter((v) => {
    if (filtroEstado !== 'todos' && v.estado_pago !== filtroEstado) return false;
    if (filtroLote !== 'todos' && v.lote_id !== filtroLote) return false;
    if (busqueda) {
      const search = busqueda.toLowerCase();
      return (
        v.cliente.toLowerCase().includes(search) ||
        v.no_conduce.toLowerCase().includes(search)
      );
    }
    return true;
  });

  // Calculate totals
  const totalFacturas = ventasFiltradas.length;
  const totalPollos = ventasFiltradas.reduce((sum, v) => sum + v.cantidad_pollos, 0);
  const totalVentas = ventasFiltradas.reduce((sum, v) => sum + v.total_factura, 0);
  const totalPendiente = ventasFiltradas.filter(v => !v.pagada).reduce((sum, v) => sum + (v.saldo_pendiente || v.total_factura), 0);
  const totalVencido = ventasFiltradas.filter(v => v.estado_pago === 'vencida').reduce((sum, v) => sum + (v.saldo_pendiente || v.total_factura), 0);

  const getEstadoBadge = (estado, saldo_pendiente) => {
    switch (estado) {
      case 'pagada':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle size={14} className="mr-1" /> Pagada</Badge>;
      case 'abonado':
        return <Badge className="bg-blue-100 text-blue-700"><Money size={14} className="mr-1" /> Abonado</Badge>;
      case 'pendiente':
        return <Badge className="bg-amber-100 text-amber-700"><Clock size={14} className="mr-1" /> Pendiente</Badge>;
      case 'vencida':
        return <Badge className="bg-red-100 text-red-700"><Warning size={14} className="mr-1" /> Vencida</Badge>;
      default:
        return null;
    }
  };

  const descargarPDF = async () => {
    if (filtroLote === 'todos') {
      toast.error('Seleccione un lote específico para generar el PDF');
      return;
    }
    
    try {
      toast.info('Generando PDF...');
      const token = localStorage.getItem('token');
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      
      const response = await fetch(`${API_URL}/api/reports/ventas/${filtroLote}/pdf`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Error al generar PDF');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ventas_${filtroLote}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descargado correctamente');
    } catch (error) {
      toast.error('Error al generar el PDF');
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {[1, 2, 3, 4].map((i) => <Skeleton key={i} className="h-24" />)}
          </div>
          <Skeleton className="h-96" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="ventas-page">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight uppercase" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              Ventas
            </h1>
            <p className="text-muted-foreground">
              Control de facturas y pagos de pollos vendidos
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={descargarPDF}
              disabled={filtroLote === 'todos'}
              title={filtroLote === 'todos' ? 'Seleccione un lote para generar PDF' : 'Descargar PDF de ventas'}
              data-testid="descargar-pdf-btn"
            >
              <FilePdf size={18} className="mr-2" />
              PDF
            </Button>
            <Link to="/ventas/rapida">
              <Button variant="outline" data-testid="entrada-rapida-btn">
                <Lightning size={18} className="mr-2" weight="fill" />
                Entrada Rápida
              </Button>
            </Link>
            <Link to="/ventas/nueva">
              <Button className="bg-[#E73425] hover:bg-[#D81C23]" data-testid="nueva-venta-btn">
                <Plus size={18} className="mr-2" />
                Nueva Venta
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-blue-100">
                  <Receipt size={20} className="text-blue-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Facturas</p>
                  <p className="text-xl font-bold">{totalFacturas}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-purple-100">
                  <Bird size={20} className="text-purple-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pollos Vendidos</p>
                  <p className="text-xl font-bold">{formatNumber(totalPollos)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-emerald-100">
                  <CurrencyDollar size={20} className="text-emerald-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Total Ventas</p>
                  <p className="text-xl font-bold">{formatCurrency(totalVentas)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-amber-100">
                  <Clock size={20} className="text-amber-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Pendiente</p>
                  <p className="text-xl font-bold text-amber-600">{formatCurrency(totalPendiente)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-border/60">
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-red-100">
                  <Warning size={20} className="text-red-600" />
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">Vencido</p>
                  <p className="text-xl font-bold text-red-600">{formatCurrency(totalVencido)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="border-border/60">
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1">
                <div className="relative">
                  <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Buscar por cliente o No. Conduce..."
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    className="pl-10"
                    data-testid="buscar-venta"
                  />
                </div>
              </div>
              <Select value={filtroLote} onValueChange={setFiltroLote}>
                <SelectTrigger className="w-[200px]" data-testid="filtro-lote">
                  <SelectValue placeholder="Filtrar por lote" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos los lotes</SelectItem>
                  {lotes.map((lote) => (
                    <SelectItem key={lote.id} value={lote.id}>{lote.nombre}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={filtroEstado} onValueChange={setFiltroEstado}>
                <SelectTrigger className="w-[180px]" data-testid="filtro-estado">
                  <SelectValue placeholder="Estado de pago" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos</SelectItem>
                  <SelectItem value="pagada">Pagadas</SelectItem>
                  <SelectItem value="pendiente">Pendientes</SelectItem>
                  <SelectItem value="vencida">Vencidas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Resumen por Lote */}
        {resumen && (
          <Card className="border-border/60 bg-gradient-to-r from-red-50 to-orange-50">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg">Resumen: {resumen.lote_nombre}</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-6 gap-4 text-sm">
                <div>
                  <p className="text-muted-foreground">Facturas</p>
                  <p className="font-semibold">{resumen.total_facturas}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Pollos</p>
                  <p className="font-semibold">{formatNumber(resumen.total_pollos_vendidos)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Peso Neto</p>
                  <p className="font-semibold">{formatNumber(resumen.total_peso_neto)} lb</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Peso Prom.</p>
                  <p className="font-semibold">{resumen.peso_promedio_general} lb</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Total Ventas</p>
                  <p className="font-semibold text-emerald-600">{formatCurrency(resumen.total_ventas)}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Por Cobrar</p>
                  <p className="font-semibold text-amber-600">{formatCurrency(resumen.total_pendiente)}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Ventas Table */}
        <Card className="border-border/60">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Receipt size={24} className="text-[#F69031]" />
              Listado de Ventas
            </CardTitle>
            <CardDescription>
              {ventasFiltradas.length} venta(s) encontrada(s)
            </CardDescription>
          </CardHeader>
          <CardContent>
            {ventasFiltradas.length === 0 ? (
              <div className="empty-state py-12">
                <Receipt size={64} className="text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-semibold mb-2">No hay ventas</h3>
                <p className="text-muted-foreground mb-6">
                  {busqueda || filtroEstado !== 'todos' || filtroLote !== 'todos'
                    ? 'No se encontraron ventas con los filtros aplicados'
                    : 'Registre su primera venta para comenzar'}
                </p>
                <Link to="/ventas/nueva">
                  <Button className="bg-[#E73425] hover:bg-[#D81C23]">
                    <Plus size={18} className="mr-2" />
                    Nueva Venta
                  </Button>
                </Link>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-3 w-10">
                        <input
                          type="checkbox"
                          checked={facturasSeleccionadas.length === ventasFiltradas.length && ventasFiltradas.length > 0}
                          onChange={seleccionarTodas}
                          className="w-4 h-4 rounded border-gray-300 text-[#E73425] focus:ring-[#E73425]"
                          title="Seleccionar todas"
                        />
                      </th>
                      <th className="p-3 text-left font-semibold">No. Conduce</th>
                      <th className="p-3 text-left font-semibold">Cliente</th>
                      <th className="p-3 text-left font-semibold">Fecha</th>
                      <th className="p-3 text-right font-semibold">Pollos</th>
                      <th className="p-3 text-right font-semibold">Promedio</th>
                      <th className="p-3 text-right font-semibold">P. Neto</th>
                      <th className="p-3 text-right font-semibold">Precio</th>
                      <th className="p-3 text-right font-semibold">Total</th>
                      <th className="p-3 text-right font-semibold">Pagado</th>
                      <th className="p-3 text-right font-semibold">Saldo</th>
                      <th className="p-3 text-center font-semibold">Estado</th>
                      <th className="p-3 text-center font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ventasFiltradas.map((venta) => {
                      const pesoPromedio = venta.cantidad_pollos > 0 
                        ? (venta.peso_neto / venta.cantidad_pollos).toFixed(2) 
                        : '0.00';
                      const isSelected = facturasSeleccionadas.some(f => f.id === venta.id);
                      return (
                      <React.Fragment key={venta.id}>
                      <tr 
                        className={`border-b hover:bg-muted/30 transition-colors cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                        onClick={(e) => {
                          // No abrir modal si se hizo clic en checkbox, botón o popover
                          if (e.target.closest('input, button, [role="button"], [data-radix-popper-content-wrapper]')) return;
                          setVentaFactura(venta);
                          setShowFacturaModal(true);
                        }}
                      >
                        <td className="p-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleFacturaSeleccion(venta)}
                            className="w-4 h-4 rounded border-gray-300 text-[#E73425] focus:ring-[#E73425]"
                          />
                        </td>
                        <td className="p-3 font-medium">
                          <button 
                            className="flex items-center gap-1 hover:text-[#E73425] transition-colors"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedRowId(expandedRowId === venta.id ? null : venta.id);
                            }}
                          >
                            <CaretDown 
                              size={16} 
                              className={`transition-transform ${expandedRowId === venta.id ? 'rotate-180' : ''}`}
                            />
                            {venta.no_conduce}
                          </button>
                        </td>
                        <td className="p-3">{venta.cliente}</td>
                        <td className="p-3">{new Date(venta.fecha).toLocaleDateString('es-DO', { day: '2-digit', month: '2-digit', year: 'numeric' })}</td>
                        <td className="p-3 text-right">{formatNumber(venta.cantidad_pollos)}</td>
                        <td className="p-3 text-right font-mono text-blue-600">{pesoPromedio} lb</td>
                        <td className="p-3 text-right font-mono text-emerald-600">{formatNumber(venta.peso_neto)} lb</td>
                        <td className="p-3 text-right font-mono text-orange-600">${venta.precio_libra?.toFixed(2) || '0.00'}</td>
                        <td className="p-3 text-right font-semibold">{formatCurrency(venta.total_factura)}</td>
                        <td className="p-3 text-right">
                          {/* Columna Pagado con detalle de pagos */}
                          {venta.pagos && venta.pagos.length > 0 ? (
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="text-emerald-600 hover:text-emerald-700 font-medium flex items-center justify-end gap-1 w-full cursor-pointer hover:underline">
                                  {formatCurrency(venta.monto_pagado || 0)}
                                  <ListBullets size={14} />
                                </button>
                              </PopoverTrigger>
                              <PopoverContent className="w-80" align="end">
                                <div className="space-y-3">
                                  <div className="flex items-center gap-2 border-b pb-2">
                                    <Money size={20} className="text-emerald-600" />
                                    <h4 className="font-semibold">Historial de Pagos</h4>
                                  </div>
                                  <div className="space-y-2 max-h-48 overflow-y-auto">
                                    {venta.pagos.map((pago, idx) => (
                                      <div key={pago.id || idx} className="bg-muted/50 rounded-lg p-2 text-sm">
                                        <div className="flex justify-between items-start">
                                          <div>
                                            <p className="font-medium text-emerald-600">{formatCurrency(pago.monto)}</p>
                                            <p className="text-xs text-muted-foreground">
                                              {new Date(pago.fecha).toLocaleDateString('es-DO', { 
                                                day: '2-digit', 
                                                month: 'short', 
                                                year: 'numeric',
                                                hour: '2-digit',
                                                minute: '2-digit'
                                              })}
                                            </p>
                                          </div>
                                          <div className="flex items-center gap-1">
                                            <Button
                                              size="sm"
                                              variant="ghost"
                                              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                              onClick={() => abrirEditarPago(venta, pago)}
                                              title="Editar pago"
                                            >
                                              <PencilSimple size={14} />
                                            </Button>
                                            <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded">
                                              #{idx + 1}
                                            </span>
                                          </div>
                                        </div>
                                        {pago.nota && (
                                          <p className="text-xs text-muted-foreground mt-1 italic">
                                            "{pago.nota}"
                                          </p>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                  <div className="border-t pt-2 flex justify-between text-sm">
                                    <span className="text-muted-foreground">Total Pagado:</span>
                                    <span className="font-bold text-emerald-600">{formatCurrency(venta.monto_pagado || 0)}</span>
                                  </div>
                                </div>
                              </PopoverContent>
                            </Popover>
                          ) : (
                            <span className="text-emerald-600">{formatCurrency(venta.monto_pagado || 0)}</span>
                          )}
                        </td>
                        <td className="p-3 text-right text-amber-600 font-semibold">
                          {formatCurrency(venta.saldo_pendiente || 0)}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            {getEstadoBadge(venta.estado_pago, venta.saldo_pendiente)}
                            {!venta.pagada && venta.dias_atraso > 0 && (
                              <span className="text-xs text-red-600">{venta.dias_atraso} días atraso</span>
                            )}
                            {venta.pagos && venta.pagos.length > 0 && !venta.pagada && (
                              <span className="text-xs text-blue-600">{venta.pagos.length} abono(s)</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="flex items-center justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-blue-600 hover:text-blue-700"
                              onClick={() => abrirModalEdicion(venta)}
                              data-testid={`editar-${venta.id}`}
                              title="Editar"
                            >
                              <PencilSimple size={16} />
                            </Button>
                            {!venta.pagada && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="h-8 px-2 text-emerald-600 hover:text-emerald-700"
                                onClick={() => abrirModalPago(venta)}
                                data-testid={`pagar-${venta.id}`}
                              >
                                <Money size={16} className="mr-1" />
                                Abonar
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost" 
                              className="h-8 w-8 p-0"
                              onClick={() => {
                                setVentaFactura(venta);
                                setShowFacturaModal(true);
                              }}
                              title="Ver Factura"
                            >
                              <Eye size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-blue-500 hover:text-blue-600"
                              onClick={() => handlePrintFactura(venta)}
                              title="Imprimir Factura"
                            >
                              <Printer size={16} />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 w-8 p-0 text-red-500 hover:text-red-600"
                              onClick={() => handleDelete(venta.id)}
                              title="Eliminar"
                            >
                              <Trash size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                      {/* Fila expandida con detalles */}
                      {expandedRowId === venta.id && (
                        <tr className="bg-slate-50 border-b">
                          <td colSpan={13} className="p-4">
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                              {/* Detalles de la venta */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Detalles de Venta</h4>
                                <div className="bg-white rounded-lg p-4 border space-y-2">
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Lote:</span>
                                    <span className="font-medium">{venta.lote_nombre}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Total Jaulas:</span>
                                    <span className="font-medium">{venta.total_jaulas}</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Peso Bruto:</span>
                                    <span className="font-medium">{formatNumber(venta.peso_bruto)} lb</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Peso Tara:</span>
                                    <span className="font-medium">{formatNumber(venta.peso_tara)} lb</span>
                                  </div>
                                  <div className="flex justify-between">
                                    <span className="text-muted-foreground">Días Crédito:</span>
                                    <span className="font-medium">{venta.dias_credito || 0}</span>
                                  </div>
                                  {venta.fecha_vencimiento && (
                                    <div className="flex justify-between">
                                      <span className="text-muted-foreground">Vencimiento:</span>
                                      <span className={`font-medium ${venta.dias_atraso > 0 ? 'text-red-600' : ''}`}>
                                        {new Date(venta.fecha_vencimiento).toLocaleDateString('es-DO')}
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Resumen financiero */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Resumen Financiero</h4>
                                <div className="bg-white rounded-lg p-4 border space-y-2">
                                  <div className="flex justify-between text-lg">
                                    <span className="text-muted-foreground">Total Factura:</span>
                                    <span className="font-bold">{formatCurrency(venta.total_factura)}</span>
                                  </div>
                                  <div className="flex justify-between text-emerald-600">
                                    <span>Monto Pagado:</span>
                                    <span className="font-semibold">{formatCurrency(venta.monto_pagado || 0)}</span>
                                  </div>
                                  <div className="flex justify-between text-amber-600">
                                    <span>Saldo Pendiente:</span>
                                    <span className="font-semibold">{formatCurrency(venta.saldo_pendiente || 0)}</span>
                                  </div>
                                  {venta.dias_atraso > 0 && (
                                    <div className="mt-2 p-2 bg-red-50 rounded text-red-600 text-sm text-center">
                                      ⚠️ {venta.dias_atraso} días de atraso
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              {/* Acciones rápidas */}
                              <div className="space-y-3">
                                <h4 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">Acciones</h4>
                                <div className="bg-white rounded-lg p-4 border space-y-2">
                                  <Button 
                                    className="w-full justify-start gap-2" 
                                    variant="outline"
                                    onClick={() => {
                                      setVentaFactura(venta);
                                      setShowFacturaModal(true);
                                    }}
                                  >
                                    <Eye size={16} /> Ver Factura
                                  </Button>
                                  <Button 
                                    className="w-full justify-start gap-2" 
                                    variant="outline"
                                    onClick={() => handlePrintFactura(venta)}
                                  >
                                    <Printer size={16} /> Imprimir PDF
                                  </Button>
                                  {!venta.pagada && (
                                    <Button 
                                      className="w-full justify-start gap-2 bg-emerald-600 hover:bg-emerald-700 text-white"
                                      onClick={() => abrirModalPago(venta)}
                                    >
                                      <Money size={16} /> Registrar Pago
                                    </Button>
                                  )}
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                      </React.Fragment>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-semibold">
                      <td className="p-3"></td>
                      <td className="p-3" colSpan={3}>TOTALES</td>
                      <td className="p-3 text-right">{formatNumber(totalPollos)}</td>
                      <td className="p-3 text-right"></td>
                      <td className="p-3 text-right text-emerald-600">{formatNumber(ventasFiltradas.reduce((s, v) => s + (v.peso_neto || 0), 0))} lb</td>
                      <td className="p-3 text-right"></td>
                      <td className="p-3 text-right text-[#E73425]">{formatCurrency(totalVentas)}</td>
                      <td className="p-3 text-right text-emerald-600">
                        {formatCurrency(ventasFiltradas.reduce((s, v) => s + (v.monto_pagado || 0), 0))}
                      </td>
                      <td className="p-3 text-right text-amber-600">{formatCurrency(totalPendiente)}</td>
                      <td className="p-3" colSpan={2}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Panel flotante de facturas seleccionadas - usando Portal para renderizar fuera del contenedor */}
        {facturasSeleccionadas.length > 0 && ReactDOM.createPortal(
          <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-[9999]" style={{ marginLeft: '0' }}>
            <Card className="shadow-2xl border-2 border-[#E73425] bg-white animate-fade-in">
              <CardContent className="p-4">
                <div className="flex items-center gap-6 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <div className="bg-[#E73425] text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">
                      {resumenSeleccion.cantidad}
                    </div>
                    <span className="font-semibold">facturas seleccionadas</span>
                  </div>
                  
                  <div className="h-8 w-px bg-gray-200"></div>
                  
                  <div className="flex gap-4 text-sm">
                    <div>
                      <span className="text-muted-foreground">Total:</span>
                      <span className="font-bold ml-1">{formatCurrency(resumenSeleccion.totalFacturado)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Pagado:</span>
                      <span className="font-bold text-emerald-600 ml-1">{formatCurrency(resumenSeleccion.totalPagado)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Pendiente:</span>
                      <span className="font-bold text-amber-600 ml-1">{formatCurrency(resumenSeleccion.totalPendiente)}</span>
                    </div>
                  </div>
                  
                  <div className="h-8 w-px bg-gray-200"></div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowResumenSeleccion(true)}
                    >
                      <Eye size={16} className="mr-1" />
                      Ver Detalle
                    </Button>
                    <Button
                      size="sm"
                      className="bg-[#E73425] hover:bg-[#D81C23]"
                      onClick={handleGenerarEstadoCuenta}
                    >
                      <FilePdf size={16} className="mr-1" />
                      Estado de Cuenta
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={limpiarSeleccion}
                    >
                      <Trash size={16} />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>,
          document.body
        )}

        {/* Modal de Resumen de Selección */}
        <Dialog open={showResumenSeleccion} onOpenChange={setShowResumenSeleccion}>
          <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt size={24} className="text-[#E73425]" />
                Estado de Cuenta - {resumenSeleccion.cantidad} Facturas
              </DialogTitle>
              <DialogDescription>
                Resumen de las facturas seleccionadas para enviar al cliente
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              {/* Resumen General */}
              <div className="grid grid-cols-3 gap-4">
                <div className="bg-slate-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Facturado</p>
                  <p className="text-2xl font-bold text-[#E73425]">{formatCurrency(resumenSeleccion.totalFacturado)}</p>
                </div>
                <div className="bg-emerald-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">Total Pagado</p>
                  <p className="text-2xl font-bold text-emerald-600">{formatCurrency(resumenSeleccion.totalPagado)}</p>
                </div>
                <div className="bg-amber-50 rounded-lg p-4 text-center">
                  <p className="text-sm text-muted-foreground">Saldo Pendiente</p>
                  <p className="text-2xl font-bold text-amber-600">{formatCurrency(resumenSeleccion.totalPendiente)}</p>
                </div>
              </div>

              {/* Tabla de facturas seleccionadas */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="p-3 text-left">No. Conduce</th>
                      <th className="p-3 text-left">Fecha</th>
                      <th className="p-3 text-right">Total</th>
                      <th className="p-3 text-right">Pagado</th>
                      <th className="p-3 text-right">Pendiente</th>
                      <th className="p-3 text-center">Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {facturasSeleccionadas.map((f) => (
                      <tr key={f.id} className="border-t">
                        <td className="p-3 font-medium">{f.no_conduce}</td>
                        <td className="p-3">{new Date(f.fecha).toLocaleDateString('es-DO')}</td>
                        <td className="p-3 text-right font-semibold">{formatCurrency(f.total_factura)}</td>
                        <td className="p-3 text-right text-emerald-600">{formatCurrency(f.monto_pagado || 0)}</td>
                        <td className="p-3 text-right text-amber-600">{formatCurrency(f.saldo_pendiente || 0)}</td>
                        <td className="p-3 text-center">
                          {f.pagada ? (
                            <Badge className="bg-emerald-100 text-emerald-700">Pagada</Badge>
                          ) : f.monto_pagado > 0 ? (
                            <Badge className="bg-blue-100 text-blue-700">Abonada</Badge>
                          ) : (
                            <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-muted/50 font-semibold">
                      <td className="p-3" colSpan={2}>TOTALES</td>
                      <td className="p-3 text-right text-[#E73425]">{formatCurrency(resumenSeleccion.totalFacturado)}</td>
                      <td className="p-3 text-right text-emerald-600">{formatCurrency(resumenSeleccion.totalPagado)}</td>
                      <td className="p-3 text-right text-amber-600">{formatCurrency(resumenSeleccion.totalPendiente)}</td>
                      <td className="p-3"></td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Info adicional */}
              <div className="bg-muted/30 rounded-lg p-4">
                <p className="text-sm text-muted-foreground">
                  <strong>Pollos:</strong> {formatNumber(resumenSeleccion.pollosTotal)} | 
                  <strong className="ml-2">Peso Neto:</strong> {formatNumber(resumenSeleccion.pesoNetoTotal)} lb
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowResumenSeleccion(false)}>
                Cerrar
              </Button>
              <Button className="bg-[#E73425] hover:bg-[#D81C23]" onClick={handleGenerarEstadoCuenta}>
                <FilePdf size={16} className="mr-2" />
                Descargar PDF
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Pago */}
        <Dialog open={showPagoModal} onOpenChange={setShowPagoModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Money size={24} className="text-emerald-600" />
                Registrar Pago / Abono
              </DialogTitle>
              <DialogDescription>
                Ingrese el monto del pago o abono para esta factura
              </DialogDescription>
            </DialogHeader>
            {ventaSeleccionada && (
              <div className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Cliente:</span>
                    <span className="font-medium">{ventaSeleccionada.cliente}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">No. Conduce:</span>
                    <span className="font-medium">{ventaSeleccionada.no_conduce}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Total Factura:</span>
                    <span className="font-medium">{formatCurrency(ventaSeleccionada.total_factura)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Ya Pagado:</span>
                    <span className="font-medium text-emerald-600">{formatCurrency(ventaSeleccionada.monto_pagado || 0)}</span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="font-semibold">Saldo Pendiente:</span>
                    <span className="font-bold text-amber-600 text-lg">
                      {formatCurrency(ventaSeleccionada.saldo_pendiente || ventaSeleccionada.total_factura)}
                    </span>
                  </div>
                </div>

                {/* Historial de pagos */}
                {ventaSeleccionada.pagos && ventaSeleccionada.pagos.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-semibold">Historial de Pagos:</Label>
                    <div className="max-h-32 overflow-y-auto space-y-1">
                      {ventaSeleccionada.pagos.map((pago, idx) => (
                        <div key={pago.id || idx} className="flex justify-between text-xs bg-emerald-50 rounded px-2 py-1">
                          <span>{new Date(pago.fecha).toLocaleDateString('es-DO')}</span>
                          <span className="text-muted-foreground">{pago.nota}</span>
                          <span className="font-medium text-emerald-600">{formatCurrency(pago.monto)}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="monto-pago">Monto a Pagar/Abonar *</Label>
                  <Input
                    id="monto-pago"
                    type="number"
                    step="0.01"
                    value={montoPago}
                    onChange={(e) => setMontoPago(e.target.value)}
                    placeholder="0.00"
                    data-testid="monto-pago-input"
                  />
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMontoPago((ventaSeleccionada.saldo_pendiente || ventaSeleccionada.total_factura).toString())}
                    >
                      Pago Completo
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setMontoPago(((ventaSeleccionada.saldo_pendiente || ventaSeleccionada.total_factura) / 2).toFixed(2))}
                    >
                      50%
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="nota-pago">Nota (opcional)</Label>
                  <Textarea
                    id="nota-pago"
                    value={notaPago}
                    onChange={(e) => setNotaPago(e.target.value)}
                    placeholder="Ej: Abono en efectivo, Transferencia #123..."
                    rows={2}
                  />
                </div>
              </div>
            )}
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={cerrarModalPago}>
                Cancelar
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handlePagar}
                disabled={loadingPago || !montoPago}
              >
                {loadingPago ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Registrar Pago
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Edición con validación de Super Usuario */}
        <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
          <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ShieldCheck size={24} className="text-[#F69031]" />
                Editar Venta
              </DialogTitle>
              <DialogDescription>
                {!superuserValidated 
                  ? 'Ingrese su código de super usuario para continuar'
                  : 'Modifique los datos de la venta'}
              </DialogDescription>
            </DialogHeader>
            
            {ventaAEditar && (
              <div className="space-y-4">
                {/* Info de la venta */}
                <div className="bg-muted/50 rounded-lg p-3 flex justify-between items-center">
                  <div>
                    <p className="font-semibold">{ventaAEditar.no_conduce}</p>
                    <p className="text-sm text-muted-foreground">{ventaAEditar.cliente}</p>
                  </div>
                  <Badge className="bg-blue-100 text-blue-700">
                    <PencilSimple size={14} className="mr-1" />
                    Editando
                  </Badge>
                </div>

                {/* Paso 1: Validar código de super usuario */}
                {!superuserValidated && (
                  <div className="space-y-4 border border-amber-200 bg-amber-50 rounded-lg p-4">
                    <p className="text-sm text-amber-700">
                      Para modificar esta venta, ingrese su código de super usuario.
                    </p>
                    <div className="space-y-2">
                      <Label htmlFor="superuser-code">Código de Super Usuario</Label>
                      <Input
                        id="superuser-code"
                        type="password"
                        value={superuserCode}
                        onChange={(e) => setSuperuserCode(e.target.value)}
                        placeholder="Ingrese su código secreto"
                        data-testid="edit-superuser-code"
                        onKeyDown={(e) => e.key === 'Enter' && handleValidateSuperuser()}
                      />
                    </div>
                    <Button
                      onClick={handleValidateSuperuser}
                      disabled={validatingSuperuser || !superuserCode}
                      className="w-full bg-[#F69031] hover:bg-[#E58020]"
                    >
                      {validatingSuperuser ? (
                        <div className="spinner h-4 w-4" />
                      ) : (
                        <>
                          <ShieldCheck size={18} className="mr-2" />
                          Validar Código
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Paso 2: Formulario de edición */}
                {superuserValidated && (
                  <div className="space-y-4 animate-fade-in">
                    <div className="flex items-center gap-2 text-emerald-600 text-sm">
                      <CheckCircle size={18} />
                      <span>Código validado - puede editar los datos</span>
                    </div>

                    {/* Cliente y Fecha */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-cliente">Cliente</Label>
                        <Input
                          id="edit-cliente"
                          value={editFormData.cliente || ''}
                          onChange={(e) => handleEditFormChange('cliente', e.target.value)}
                          data-testid="edit-cliente"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-fecha">Fecha</Label>
                        <Input
                          id="edit-fecha"
                          type="date"
                          value={editFormData.fecha || ''}
                          onChange={(e) => handleEditFormChange('fecha', e.target.value)}
                          data-testid="edit-fecha"
                        />
                      </div>
                    </div>

                    {/* Jaulas y Pollos */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-jaulas">Total Jaulas</Label>
                        <Input
                          id="edit-jaulas"
                          type="number"
                          value={editFormData.total_jaulas || ''}
                          onChange={(e) => handleEditFormChange('total_jaulas', e.target.value)}
                          data-testid="edit-jaulas"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-pollos">Cantidad Pollos</Label>
                        <Input
                          id="edit-pollos"
                          type="number"
                          value={editFormData.cantidad_pollos || ''}
                          onChange={(e) => handleEditFormChange('cantidad_pollos', e.target.value)}
                          data-testid="edit-pollos"
                        />
                      </div>
                    </div>

                    {/* Pesos */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-tara">Peso Tara (lb)</Label>
                        <Input
                          id="edit-tara"
                          type="number"
                          step="0.01"
                          value={editFormData.peso_tara || ''}
                          onChange={(e) => handleEditFormChange('peso_tara', e.target.value)}
                          data-testid="edit-tara"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-bruto">Peso Bruto (lb)</Label>
                        <Input
                          id="edit-bruto"
                          type="number"
                          step="0.01"
                          value={editFormData.peso_bruto || ''}
                          onChange={(e) => handleEditFormChange('peso_bruto', e.target.value)}
                          data-testid="edit-bruto"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>Peso Neto (lb)</Label>
                        <div className="h-10 px-3 py-2 bg-muted/50 rounded-md flex items-center font-semibold text-emerald-600">
                          {formatNumber(editPesoNeto)} lb
                        </div>
                      </div>
                    </div>

                    {/* Precio y Crédito */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="edit-precio">Precio por Libra ($)</Label>
                        <Input
                          id="edit-precio"
                          type="number"
                          step="0.01"
                          value={editFormData.precio_libra || ''}
                          onChange={(e) => handleEditFormChange('precio_libra', e.target.value)}
                          data-testid="edit-precio"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="edit-credito">Días de Crédito</Label>
                        <Select
                          value={String(editFormData.dias_credito || '1')}
                          onValueChange={(value) => handleEditFormChange('dias_credito', value)}
                        >
                          <SelectTrigger data-testid="edit-credito">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="1">1 día</SelectItem>
                            <SelectItem value="3">3 días</SelectItem>
                            <SelectItem value="7">7 días</SelectItem>
                            <SelectItem value="14">14 días</SelectItem>
                            <SelectItem value="21">21 días</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Total calculado */}
                    <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-lg p-4 border border-[#E73425]/20">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm text-muted-foreground">Nuevo Total</p>
                          <p className="text-xs text-muted-foreground">
                            {formatNumber(editPesoNeto)} lb × ${editFormData.precio_libra || '0'}
                          </p>
                        </div>
                        <p className="text-2xl font-bold text-[#E73425]">
                          {formatCurrency(editTotalFactura)}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={cerrarModalEdicion}>
                Cancelar
              </Button>
              {superuserValidated && (
                <Button
                  className="bg-[#E73425] hover:bg-[#D81C23]"
                  onClick={handleSaveEdit}
                  disabled={savingEdit}
                  data-testid="guardar-edicion"
                >
                  {savingEdit ? (
                    <div className="spinner h-4 w-4" />
                  ) : (
                    <>
                      <CheckCircle size={16} className="mr-2" />
                      Guardar Cambios
                    </>
                  )}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Edición de Pago */}
        <Dialog open={showEditPagoModal} onOpenChange={setShowEditPagoModal}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Money size={20} className="text-emerald-600" />
                Editar Pago
              </DialogTitle>
              <DialogDescription>
                Modifique los datos del pago. Requiere código de super usuario.
              </DialogDescription>
            </DialogHeader>

            {pagoAEditar && ventaDelPago && (
              <div className="space-y-4">
                {/* Info del pago */}
                <div className="bg-muted/50 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-muted-foreground">Conduce #{ventaDelPago.no_conduce}</p>
                      <p className="font-medium">{ventaDelPago.cliente}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Pago original</p>
                      <p className="font-semibold text-emerald-600">{formatCurrency(pagoAEditar.monto)}</p>
                    </div>
                  </div>
                </div>

                {/* Código de super usuario */}
                <div className="space-y-2">
                  <Label htmlFor="edit-pago-code">Código de Super Usuario *</Label>
                  <Input
                    id="edit-pago-code"
                    type="password"
                    value={editPagoCode}
                    onChange={(e) => setEditPagoCode(e.target.value)}
                    placeholder="Ingrese su código"
                    data-testid="edit-pago-superuser-code"
                  />
                </div>

                {/* Monto */}
                <div className="space-y-2">
                  <Label htmlFor="edit-pago-monto">Nuevo Monto ($)</Label>
                  <Input
                    id="edit-pago-monto"
                    type="number"
                    step="0.01"
                    value={editPagoMonto}
                    onChange={(e) => setEditPagoMonto(e.target.value)}
                    data-testid="edit-pago-monto"
                  />
                </div>

                {/* Nota */}
                <div className="space-y-2">
                  <Label htmlFor="edit-pago-nota">Nota (opcional)</Label>
                  <Input
                    id="edit-pago-nota"
                    value={editPagoNota}
                    onChange={(e) => setEditPagoNota(e.target.value)}
                    placeholder="Agregar nota"
                    data-testid="edit-pago-nota"
                  />
                </div>
              </div>
            )}

            <DialogFooter className="gap-2 flex-col sm:flex-row">
              <Button 
                variant="destructive" 
                onClick={handleEliminarPago}
                disabled={savingPagoEdit || !editPagoCode}
                className="sm:mr-auto"
              >
                <Trash size={16} className="mr-2" />
                Eliminar Pago
              </Button>
              <Button variant="outline" onClick={cerrarEditarPago}>
                Cancelar
              </Button>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700"
                onClick={handleEditarPago}
                disabled={savingPagoEdit || !editPagoCode || !editPagoMonto}
              >
                {savingPagoEdit ? (
                  <div className="spinner h-4 w-4" />
                ) : (
                  <>
                    <CheckCircle size={16} className="mr-2" />
                    Guardar
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Modal de Factura */}
        <Dialog open={showFacturaModal} onOpenChange={setShowFacturaModal}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            {ventaFactura && (
              <>
                <DialogHeader className="border-b pb-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <DialogTitle className="text-2xl font-bold text-[#E73425]">
                        FACTURA
                      </DialogTitle>
                      <DialogDescription className="text-base">
                        Conduce No. {ventaFactura.no_conduce}
                      </DialogDescription>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">ELANCO GROUP</p>
                      <p className="text-xs text-muted-foreground">Sistema de Gestión Avícola</p>
                    </div>
                  </div>
                </DialogHeader>

                <div className="space-y-6 py-4">
                  {/* Info Cliente y Fecha */}
                  <div className="grid grid-cols-2 gap-4 bg-muted/30 rounded-lg p-4">
                    <div>
                      <p className="text-sm text-muted-foreground">Cliente</p>
                      <p className="font-semibold text-lg">{ventaFactura.cliente}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-muted-foreground">Fecha</p>
                      <p className="font-semibold">
                        {new Date(ventaFactura.fecha).toLocaleDateString('es-DO', {
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>

                  {/* Detalle de Venta */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50">
                        <tr>
                          <th className="p-3 text-left">Descripción</th>
                          <th className="p-3 text-right">Cantidad</th>
                          <th className="p-3 text-right">Precio Unit.</th>
                          <th className="p-3 text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr className="border-t">
                          <td className="p-3">
                            <div>
                              <p className="font-medium">Pollos de Engorde</p>
                              <p className="text-xs text-muted-foreground">
                                {ventaFactura.total_jaulas} jaulas | Peso Neto: {formatNumber(ventaFactura.peso_neto)} lb
                              </p>
                            </div>
                          </td>
                          <td className="p-3 text-right">{formatNumber(ventaFactura.cantidad_pollos)} uds</td>
                          <td className="p-3 text-right">${ventaFactura.precio_libra?.toFixed(2)}/lb</td>
                          <td className="p-3 text-right font-semibold">{formatCurrency(ventaFactura.total_factura)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* Detalles adicionales */}
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="bg-blue-50 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground">Promedio/Pollo</p>
                      <p className="font-bold text-blue-600 text-lg">
                        {ventaFactura.cantidad_pollos > 0 
                          ? (ventaFactura.peso_neto / ventaFactura.cantidad_pollos).toFixed(2) 
                          : '0.00'} lb
                      </p>
                    </div>
                    <div className="bg-orange-50 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground">Peso Bruto</p>
                      <p className="font-bold text-orange-600 text-lg">{formatNumber(ventaFactura.peso_bruto)} lb</p>
                    </div>
                    <div className="bg-slate-50 rounded-lg p-3 text-center">
                      <p className="text-muted-foreground">Tara</p>
                      <p className="font-bold text-slate-600 text-lg">{formatNumber(ventaFactura.peso_tara)} lb</p>
                    </div>
                  </div>

                  {/* Resumen de Pago */}
                  <div className="border-t pt-4 space-y-2">
                    <div className="flex justify-between text-lg">
                      <span className="font-semibold">Total Factura:</span>
                      <span className="font-bold text-[#E73425]">{formatCurrency(ventaFactura.total_factura)}</span>
                    </div>
                    <div className="flex justify-between text-emerald-600">
                      <span>Monto Pagado:</span>
                      <span className="font-semibold">{formatCurrency(ventaFactura.monto_pagado || 0)}</span>
                    </div>
                    <div className="flex justify-between text-amber-600">
                      <span>Saldo Pendiente:</span>
                      <span className="font-semibold">{formatCurrency(ventaFactura.saldo_pendiente || 0)}</span>
                    </div>
                  </div>

                  {/* Historial de Pagos si existe */}
                  {ventaFactura.pagos && ventaFactura.pagos.length > 0 && (
                    <div className="border-t pt-4">
                      <p className="font-semibold mb-2 flex items-center gap-2">
                        <Money size={18} className="text-emerald-600" />
                        Historial de Pagos
                      </p>
                      <div className="space-y-2">
                        {ventaFactura.pagos.map((pago, idx) => (
                          <div key={pago.id || idx} className="flex justify-between items-center bg-emerald-50 rounded p-2 text-sm">
                            <div>
                              <span className="text-muted-foreground">
                                {new Date(pago.fecha).toLocaleDateString('es-DO', {
                                  day: '2-digit',
                                  month: 'short',
                                  year: 'numeric'
                                })}
                              </span>
                              {pago.nota && <span className="ml-2 text-xs italic">"{pago.nota}"</span>}
                            </div>
                            <span className="font-semibold text-emerald-600">{formatCurrency(pago.monto)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Estado */}
                  <div className="flex justify-center pt-2">
                    {ventaFactura.pagada ? (
                      <Badge className="bg-emerald-100 text-emerald-700 text-lg px-6 py-2">
                        <CheckCircle size={20} className="mr-2" />
                        PAGADA
                      </Badge>
                    ) : ventaFactura.monto_pagado > 0 ? (
                      <Badge className="bg-blue-100 text-blue-700 text-lg px-6 py-2">
                        <Clock size={20} className="mr-2" />
                        ABONADA
                      </Badge>
                    ) : (
                      <Badge className="bg-amber-100 text-amber-700 text-lg px-6 py-2">
                        <Warning size={20} className="mr-2" />
                        PENDIENTE
                      </Badge>
                    )}
                  </div>
                </div>

                <DialogFooter className="border-t pt-4">
                  <Button variant="outline" onClick={() => setShowFacturaModal(false)}>
                    Cerrar
                  </Button>
                  <Button 
                    className="bg-[#E73425] hover:bg-[#D81C23]"
                    onClick={() => {
                      // Print functionality
                      window.print();
                    }}
                  >
                    <FilePdf size={16} className="mr-2" />
                    Imprimir
                  </Button>
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
